import React, { useEffect, useMemo, useState } from 'react';

export const EMPTY_FILTER_VALUE = '__SORTABLE_TABLE_EMPTY__';

const DATE_KEY_PATTERN = /(date|datetime|created|updated|deleted|launched|terminated|_at$)/i;

const TEXT_OPERATORS = [
  { value: 'equals', label: 'Égal à' },
  { value: 'notEquals', label: 'Différent de' },
  { value: 'startsWith', label: 'Commence par' },
  { value: 'notStartsWith', label: 'Ne commence pas par' },
  { value: 'endsWith', label: 'Finit par' },
  { value: 'notEndsWith', label: 'Ne finit pas par' },
  { value: 'contains', label: 'Contient' },
  { value: 'notContains', label: 'Ne contient pas' },
];

const NUMBER_OPERATORS = [
  { value: 'equals', label: 'Égal à' },
  { value: 'notEquals', label: 'Différent de' },
  { value: 'greaterThan', label: 'Supérieur à' },
  { value: 'greaterThanOrEqual', label: 'Supérieur ou égal à' },
  { value: 'lessThan', label: 'Inférieur à' },
  { value: 'lessThanOrEqual', label: 'Inférieur ou égal à' },
  { value: 'between', label: 'Entre' },
];

const DATE_OPERATORS = [
  { value: 'equals', label: 'Jour précis' },
  { value: 'before', label: 'Avant' },
  { value: 'after', label: 'Après' },
  { value: 'between', label: 'Entre' },
];

const toStartOfLocalDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const formatDateInput = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isBlankValue = (value) => (
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '')
);

export const getValueKey = (value) => (
  isBlankValue(value) ? EMPTY_FILTER_VALUE : String(value)
);

export const getValueLabel = (value) => (
  value === EMPTY_FILTER_VALUE || isBlankValue(value) ? '(Vides)' : String(value)
);

export const parseNumberValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (isBlankValue(value)) return null;

  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};

export const parseDateValue = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (isBlankValue(value)) return null;
  if (typeof value === 'number') return null;

  const raw = String(value).trim();
  const dateInputMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateInputMatch) {
    const [, year, month, day] = dateInputMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime()) && /\d{4}/.test(raw)) {
    return direct;
  }

  const frenchDateMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (frenchDateMatch) {
    const [, day, month, year, hour = '0', minute = '0'] = frenchDateMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const getDateDayTimestamp = (value) => {
  const date = parseDateValue(value);
  return date ? toStartOfLocalDay(date).getTime() : null;
};

export const getColumnFilterType = (column, data) => {
  if (column.filterType) {
    if (column.filterType === 'datetime') return 'date';
    if (column.filterType === 'numeric') return 'number';
    return column.filterType;
  }

  const samples = [];
  for (let i = 0; i < data.length && samples.length < 12; i++) {
    const value = getRawCellValue(data[i], column);
    if (!isBlankValue(value)) samples.push(value);
  }

  if (samples.length === 0) return 'text';

  const keyLooksLikeDate = DATE_KEY_PATTERN.test(column.key || '');
  const dateSamples = samples.filter(value => parseDateValue(value));
  const hasDateObjects = samples.some(value => value instanceof Date);
  if ((hasDateObjects && dateSamples.length === samples.length) || (keyLooksLikeDate && dateSamples.length / samples.length >= 0.6)) {
    return 'date';
  }

  const numberSamples = samples.filter(value => parseNumberValue(value) !== null);
  if (numberSamples.length / samples.length >= 0.8) {
    return 'number';
  }

  return 'text';
};

export const getRawCellValue = (item, column) => {
  if (column?.rawValue && typeof column.rawValue === 'function') {
    return column.rawValue(item);
  }

  return item?.[column?.key];
};

const normalizeText = (value) => String(value ?? '').toLowerCase();

const isOperatorFilterReady = (filter) => {
  if (!filter || filter.type !== 'operator') return false;
  if (filter.operator === 'between') {
    return !isBlankValue(filter.value) && !isBlankValue(filter.valueTo);
  }

  return !isBlankValue(filter.value);
};

export const isFilterActive = (filter) => {
  if (!filter) return false;
  if (filter.type === 'values') return true;
  return isOperatorFilterReady(filter);
};

const compareTextFilter = (rawValue, filter) => {
  const value = normalizeText(rawValue);
  const target = normalizeText(filter.value);

  switch (filter.operator) {
    case 'equals':
      return value === target;
    case 'notEquals':
      return value !== target;
    case 'startsWith':
      return value.startsWith(target);
    case 'notStartsWith':
      return !value.startsWith(target);
    case 'endsWith':
      return value.endsWith(target);
    case 'notEndsWith':
      return !value.endsWith(target);
    case 'contains':
      return value.includes(target);
    case 'notContains':
      return !value.includes(target);
    default:
      return true;
  }
};

const compareNumberFilter = (rawValue, filter) => {
  const value = parseNumberValue(rawValue);
  const target = parseNumberValue(filter.value);
  const targetTo = parseNumberValue(filter.valueTo);

  if (value === null || target === null) return false;

  switch (filter.operator) {
    case 'equals':
      return value === target;
    case 'notEquals':
      return value !== target;
    case 'greaterThan':
      return value > target;
    case 'greaterThanOrEqual':
      return value >= target;
    case 'lessThan':
      return value < target;
    case 'lessThanOrEqual':
      return value <= target;
    case 'between':
      return targetTo !== null && value >= Math.min(target, targetTo) && value <= Math.max(target, targetTo);
    default:
      return true;
  }
};

const compareDateFilter = (rawValue, filter) => {
  const value = getDateDayTimestamp(rawValue);
  const target = getDateDayTimestamp(filter.value);
  const targetTo = getDateDayTimestamp(filter.valueTo);

  if (value === null || target === null) return false;

  switch (filter.operator) {
    case 'equals':
      return value === target;
    case 'before':
      return value < target;
    case 'after':
      return value > target;
    case 'between':
      return targetTo !== null && value >= Math.min(target, targetTo) && value <= Math.max(target, targetTo);
    default:
      return true;
  }
};

export const applyColumnFilter = (item, column, filter, filterType) => {
  if (!isFilterActive(filter)) return true;

  const rawValue = getRawCellValue(item, column);

  if (filter.type === 'values') {
    return filter.values.includes(getValueKey(rawValue));
  }

  if (filterType === 'date') return compareDateFilter(rawValue, filter);
  if (filterType === 'number') return compareNumberFilter(rawValue, filter);
  return compareTextFilter(rawValue, filter);
};

export const buildColumnOptions = (data, column) => {
  const optionMap = new Map();

  data.forEach((item) => {
    const rawValue = getRawCellValue(item, column);
    const key = getValueKey(rawValue);
    const current = optionMap.get(key);

    optionMap.set(key, {
      value: key,
      label: getValueLabel(key === EMPTY_FILTER_VALUE ? key : rawValue),
      count: current ? current.count + 1 : 1,
    });
  });

  return Array.from(optionMap.values()).sort((a, b) => {
    if (a.value === EMPTY_FILTER_VALUE) return -1;
    if (b.value === EMPTY_FILTER_VALUE) return 1;
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const getOperators = (filterType) => {
  if (filterType === 'date') return DATE_OPERATORS;
  if (filterType === 'number') return NUMBER_OPERATORS;
  return TEXT_OPERATORS;
};

const getDefaultOperator = (filterType) => {
  if (filterType === 'date') return 'equals';
  if (filterType === 'number') return 'equals';
  return 'contains';
};

const getInputType = (filterType) => {
  if (filterType === 'date') return 'date';
  if (filterType === 'number') return 'number';
  return 'text';
};

const sameValues = (left, right) => {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every(value => rightSet.has(value));
};

const getWeekRange = (baseDate) => {
  const start = toStartOfLocalDay(baseDate);
  const day = start.getDay() || 7;
  start.setDate(start.getDate() - day + 1);
  return {
    start,
    end: addDays(start, 6),
  };
};

const getShortcutFilter = (shortcut) => {
  const today = toStartOfLocalDay(new Date());

  if (shortcut === 'today') {
    return { type: 'operator', operator: 'equals', value: formatDateInput(today), valueTo: '' };
  }

  if (shortcut === 'yesterday') {
    return { type: 'operator', operator: 'equals', value: formatDateInput(addDays(today, -1)), valueTo: '' };
  }

  if (shortcut === 'thisWeek') {
    const range = getWeekRange(today);
    return { type: 'operator', operator: 'between', value: formatDateInput(range.start), valueTo: formatDateInput(range.end) };
  }

  if (shortcut === 'thisMonth') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { type: 'operator', operator: 'between', value: formatDateInput(start), valueTo: formatDateInput(end) };
  }

  if (shortcut === 'thisYear') {
    const start = new Date(today.getFullYear(), 0, 1);
    const end = new Date(today.getFullYear(), 11, 31);
    return { type: 'operator', operator: 'between', value: formatDateInput(start), valueTo: formatDateInput(end) };
  }

  return null;
};

const ShortcutButton = ({ active = false, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
      active
        ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`}
  >
    {children}
  </button>
);

const FilterModeButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1 text-[11px] font-semibold rounded-md transition-colors ${
      active
        ? 'text-blue-700 bg-blue-50 dark:text-blue-200 dark:bg-blue-900/40'
        : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
    }`}
  >
    {children}
  </button>
);

function ValuesFilterPanel({ options, selectedValues, setSelectedValues }) {
  const [searchTerm, setSearchTerm] = useState('');

  const allOptionValues = useMemo(() => options.map(option => option.value), [options]);
  const hasOnlyBlankSelected = selectedValues.length === 1 && selectedValues[0] === EMPTY_FILTER_VALUE;
  const visibleOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter(option => option.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const toggleValue = (value) => {
    setSelectedValues((current) => (
      current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value]
    ));
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <ShortcutButton active={sameValues(selectedValues, allOptionValues)} onClick={() => setSelectedValues(allOptionValues)}>Tous</ShortcutButton>
        <ShortcutButton active={selectedValues.length === 0} onClick={() => setSelectedValues([])}>Aucun</ShortcutButton>
        <ShortcutButton active={hasOnlyBlankSelected} onClick={() => setSelectedValues(allOptionValues.includes(EMPTY_FILTER_VALUE) ? [EMPTY_FILTER_VALUE] : [])}>
          Vides
        </ShortcutButton>
      </div>

      <div className="relative">
        <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Rechercher une valeur..."
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
        />
      </div>

      <div className="max-h-40 overflow-auto rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30">
        {visibleOptions.length > 0 ? (
          visibleOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 cursor-pointer"
            >
              <span className="flex items-center gap-2 min-w-0">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleValue(option.value)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="truncate">{option.label}</span>
              </span>
              <span className="text-xs text-gray-400 shrink-0">{option.count}</span>
            </label>
          ))
        ) : (
          <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
            Aucune valeur
          </div>
        )}
      </div>
    </div>
  );
}

function OperatorFilterPanel({ filterType, operator, setOperator, value, setValue, valueTo, setValueTo, applyShortcut, applyBlankFilter, isShortcutActive, isBlankFilterActive }) {
  const operators = getOperators(filterType);
  const inputType = getInputType(filterType);
  const isBetween = operator === 'between';

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Condition
        </label>
        <select
          value={operator}
          onChange={(event) => setOperator(event.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
        >
          {operators.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className={`grid gap-2 ${isBetween ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {isBetween ? 'Du' : 'Valeur'}
          </label>
          <input
            type={inputType}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>

        {isBetween && (
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Au
            </label>
            <input
              type={inputType}
              value={valueTo}
              onChange={(event) => setValueTo(event.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
        )}
      </div>

      {filterType === 'date' && (
        <div className="flex flex-wrap gap-2 pt-1">
          <ShortcutButton active={isShortcutActive('today')} onClick={() => applyShortcut('today')}>Aujourd’hui</ShortcutButton>
          <ShortcutButton active={isShortcutActive('yesterday')} onClick={() => applyShortcut('yesterday')}>Hier</ShortcutButton>
          <ShortcutButton active={isShortcutActive('thisWeek')} onClick={() => applyShortcut('thisWeek')}>Cette semaine</ShortcutButton>
          <ShortcutButton active={isShortcutActive('thisMonth')} onClick={() => applyShortcut('thisMonth')}>Ce mois</ShortcutButton>
          <ShortcutButton active={isShortcutActive('thisYear')} onClick={() => applyShortcut('thisYear')}>Cette année</ShortcutButton>
          <ShortcutButton active={isBlankFilterActive} onClick={applyBlankFilter}>Vides</ShortcutButton>
        </div>
      )}
    </div>
  );
}

function SortableTableFilterPopover({
  column,
  filterType,
  options,
  filter,
  onApply,
  onClear,
  onClose,
}) {
  const allOptionValues = useMemo(() => options.map(option => option.value), [options]);
  const [mode, setMode] = useState(filterType === 'date' ? 'condition' : (filter?.type === 'operator' ? 'condition' : 'values'));
  const [selectedValues, setSelectedValues] = useState(filter?.type === 'values' ? filter.values : allOptionValues);
  const [operator, setOperator] = useState(filter?.type === 'operator' ? filter.operator : getDefaultOperator(filterType));
  const [value, setValue] = useState(filter?.type === 'operator' ? filter.value || '' : '');
  const [valueTo, setValueTo] = useState(filter?.type === 'operator' ? filter.valueTo || '' : '');

  useEffect(() => {
    if (!filter) {
      setSelectedValues(allOptionValues);
    }
  }, [allOptionValues, filter]);

  const applyValuesFilter = () => {
    if (sameValues(selectedValues, allOptionValues)) {
      onClear();
      onClose();
      return;
    }

    onApply({ type: 'values', values: selectedValues });
    onClose();
  };

  const applyOperatorFilter = () => {
    const nextFilter = { type: 'operator', operator, value, valueTo };
    if (!isOperatorFilterReady(nextFilter)) return;
    onApply(nextFilter);
    onClose();
  };

  const applyShortcut = (shortcut) => {
    const shortcutFilter = getShortcutFilter(shortcut);
    if (!shortcutFilter) return;

    setOperator(shortcutFilter.operator);
    setValue(shortcutFilter.value);
    setValueTo(shortcutFilter.valueTo);
    onApply(shortcutFilter);
    onClose();
  };

  const applyBlankFilter = () => {
    onApply({ type: 'values', values: [EMPTY_FILTER_VALUE] });
    onClose();
  };

  const isShortcutActive = (shortcut) => {
    const shortcutFilter = getShortcutFilter(shortcut);
    if (!shortcutFilter) return false;

    return operator === shortcutFilter.operator &&
      value === shortcutFilter.value &&
      valueTo === shortcutFilter.valueTo;
  };

  const handleApply = () => {
    if (mode === 'condition') {
      applyOperatorFilter();
      return;
    }

    applyValuesFilter();
  };

  return (
    <div className="w-80 max-w-[calc(100vw-2rem)] max-h-[inherit] flex flex-col">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
              Filtrer
            </div>
            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
              {column.label}
            </div>
          </div>

          {filterType !== 'date' && (
            <div className="flex shrink-0 gap-1 rounded-lg bg-gray-50 dark:bg-gray-900 p-0.5">
              <FilterModeButton active={mode === 'values'} onClick={() => setMode('values')}>
                Valeurs
              </FilterModeButton>
              <FilterModeButton active={mode === 'condition'} onClick={() => setMode('condition')}>
                Condition
              </FilterModeButton>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3 min-h-0">
        {mode === 'values' && filterType !== 'date' ? (
          <ValuesFilterPanel
            options={options}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
          />
        ) : (
          <OperatorFilterPanel
            filterType={filterType}
            operator={operator}
            setOperator={setOperator}
            value={value}
            setValue={setValue}
            valueTo={valueTo}
            setValueTo={setValueTo}
            applyShortcut={applyShortcut}
            applyBlankFilter={applyBlankFilter}
            isShortcutActive={isShortcutActive}
            isBlankFilterActive={filter?.type === 'values' && filter.values.length === 1 && filter.values[0] === EMPTY_FILTER_VALUE}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900/70 border-t border-gray-100 dark:border-gray-700 rounded-b-xl shrink-0">
        <button
          type="button"
          onClick={() => {
            onClear();
            onClose();
          }}
          className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Réinitialiser
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        >
          Appliquer
        </button>
      </div>
    </div>
  );
}

export default SortableTableFilterPopover;
